import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

interface PopoverProps {
  open: boolean
  onClose: () => void
  children: ReactNode
  width?: number
}

// Centered modal-style popover with a click-away backdrop. (Anchored
// positioning is overkill for our use; centering reads cleanly everywhere.)
export default function Popover({ open, onClose, children, width = 260 }: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && ref.current.contains(e.target as Node)) return
      onClose()
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    const t = setTimeout(() => document.addEventListener('mousedown', onDocClick), 0)
    document.addEventListener('keydown', onKey)
    return () => {
      clearTimeout(t)
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div role="dialog" style={{ position: 'fixed', inset: 0, zIndex: 1000 }}>
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div
        ref={ref}
        style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width, maxHeight: '80vh' }}
        className="bg-white border border-gray-200 rounded-lg shadow-xl p-2 overflow-y-auto"
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}
